import { useState } from 'react';

import { Button } from '../../button';

import {
  CHANGES,
  changeLabels,
  DECISIONS,
  deciders,
  decisionKinds,
  decisionLabels,
  GUARD_PROPOSALS,
  GUARD_REFINEMENT,
  guardProposalJson,
  guardProposalLabels,
  guardSummary,
  guardTransition,
  initialGuard,
  initialShape,
  initialTable,
  initialTest,
  nowRefusedLines,
  passedCount,
  REQUIREMENTS,
  requirementLabels,
  SHAPE_REFINEMENT,
  shapes,
  shapeSummary,
  shapeTransition,
  stoppedAtLabel,
  TABLE_REFINEMENT,
  tableSummary,
  tableTransition,
  TEST_REFINEMENT,
  testSummary,
  testTransition,
  toChange,
  toDecision,
  toGuardProposal,
  toRequirement,
  waitingStages,
  yesNo,
  type GuardAction,
  type ShapeAction,
  type TableAction,
  type TestAction,
} from './decide-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './decide-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function DecideTableLab() {
  const [state, setState] = useState(initialTable);
  const act = (action: TableAction) => setState((current) => tableTransition(current, action));
  const kind = decisionKinds[state.decision];
  const summary = tableSummary(state);
  return (
    <Lab
      title="Who should decide this?"
      eyebrow="Decisions · rule, step or judgment"
      className="rel-lab decide-lab"
    >
      <Predict>Who should decide this? Choose a decision, then press Reveal.</Predict>
      <div className="cb-controls">
        <label>
          Decision
          <ModelSelect
            aria-label="Decision"
            value={state.decision}
            onValueChange={(value) => act({ type: 'decision', value: toDecision(value) })}
            options={DECISIONS.map((decision) => ({
              value: decision,
              label: decisionLabels[decision],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.revealed} onClick={() => act({ type: 'reveal' })}>
          Reveal
        </Button>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.modelDecides}
          onClick={() => act({ type: 'toggle-model' })}
        >
          Let the model decide it
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      {state.revealed ? (
        <Facts
          values={[
            ['Decision', decisionLabels[state.decision]],
            ['Kind', kind],
            ['Decided by', deciders[kind]],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{TABLE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function DecideGuardLab() {
  const [state, setState] = useState(initialGuard);
  const act = (action: GuardAction) => setState((current) => guardTransition(current, action));
  const run = state.run;
  const stages = run ? run.stages : waitingStages();
  const summary = guardSummary(state);
  return (
    <Lab
      title="Which guard stops the proposal?"
      eyebrow="Decisions · guards on both sides"
      className="rel-lab decide-lab"
    >
      <Predict>
        Which guard stops the proposal? Choose a proposal, then press Send through the guards.
      </Predict>
      <div className="cb-controls">
        <label>
          Proposal
          <ModelSelect
            aria-label="Proposal"
            value={state.proposal}
            onValueChange={(value) => act({ type: 'proposal', value: toGuardProposal(value) })}
            options={GUARD_PROPOSALS.map((proposal) => ({
              value: proposal,
              label: guardProposalLabels[proposal],
            }))}
          />
        </label>
        <Button size="sm" disabled={run !== null} onClick={() => act({ type: 'send' })}>
          Send through the guards
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The proposal">
        <pre aria-label="The proposal">{guardProposalJson[state.proposal]}</pre>
      </Panel>
      <ol className="rel-steps" aria-label="Stages">
        {stages.map((stage, index) => (
          <li
            key={stage.stage}
            data-outcome={stage.outcome}
            aria-current={run?.stoppedAt === index ? 'step' : undefined}
          >
            {stage.stage}
            {run ? <small>, {stage.detail}</small> : null}
          </li>
        ))}
      </ol>
      {run ? (
        <Facts
          values={[
            ['Stopped at', stoppedAtLabel(run)],
            ['Reason', run.reason],
            ['Written', yesNo(run.written)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {run ? <p className="rel-caption">{GUARD_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function DecideShapeLab() {
  const [state, setState] = useState(initialShape);
  const act = (action: ShapeAction) => setState((current) => shapeTransition(current, action));
  const shape = shapes[state.requirement];
  const summary = shapeSummary(state);
  return (
    <Lab
      title="Which shape fits this requirement?"
      eyebrow="Decisions · the simplest shape"
      className="rel-lab decide-lab"
    >
      <Predict>Which shape fits this requirement? Choose a requirement, then press Choose.</Predict>
      <div className="cb-controls">
        <label>
          Requirement
          <ModelSelect
            aria-label="Requirement"
            value={state.requirement}
            onValueChange={(value) => act({ type: 'requirement', value: toRequirement(value) })}
            options={REQUIREMENTS.map((requirement) => ({
              value: requirement,
              label: requirementLabels[requirement],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.chosen} onClick={() => act({ type: 'choose' })}>
          Choose
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      {state.chosen ? (
        <Facts
          values={[
            ['Shape', shape.shape],
            ['Calls per message', shape.calls],
            ['Code it needs', shape.code],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.chosen ? <p className="rel-caption">{SHAPE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function DecideTestLab() {
  const [state, setState] = useState(initialTest);
  const act = (action: TestAction) => setState((current) => testTransition(current, action));
  const results = state.results;
  const summary = testSummary(state);
  return (
    <Lab
      title="Which tests pass after this change?"
      eyebrow="Decisions · a test per rule"
      className="rel-lab decide-lab"
    >
      <Predict>
        Which tests pass after this change? Choose a change, then press Run the tests.
      </Predict>
      <div className="cb-controls">
        <label>
          Change
          <ModelSelect
            aria-label="Change"
            value={state.change}
            onValueChange={(value) => act({ type: 'change', value: toChange(value) })}
            options={CHANGES.map((change) => ({ value: change, label: changeLabels[change] }))}
          />
        </label>
        <Button size="sm" disabled={results !== null} onClick={() => act({ type: 'run' })}>
          Run the tests
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Tests"
        columns={['Test', 'Result', 'Why']}
        template="minmax(0, 1.3fr) minmax(0, 0.5fr) minmax(0, 2fr)"
        rows={(results ?? []).map((result) => [
          result.name,
          result.passed ? 'pass' : 'fail',
          result.why,
        ])}
      />
      {results ? (
        <Facts
          values={[
            ['Passed', String(passedCount(results))],
            ['Failed', String(results.length - passedCount(results))],
          ]}
        />
      ) : null}
      {results && state.change === 'shorten-title' ? (
        <Panel label="Would now be refused">
          <pre aria-label="Would now be refused">{nowRefusedLines()}</pre>
        </Panel>
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{TEST_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
